import { Loader2, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/AlertDialog.jsx";
import { Card, CardContent } from "../../components/ui/Card.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { useToast } from "../../hooks/useToast.js";
import { deleteCompany, fetchCompanies } from "../../services/superAdminService.js";

export default function CompanyManagement() {
  const { toast } = useToast();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadCompanies = async (p = page) => {
    setLoading(true);
    try {
      const res = await fetchCompanies({ page: p, limit: 20, search });
      setCompanies(res.data || []);
      setPagination(res.pagination || {});
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies(1);
    setPage(1);
  }, [search]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await deleteCompany(deleteConfirm.id);
      toast({ title: res.message || "Company deleted" });
      setDeleteConfirm(null);
      loadCompanies(page);
    } catch (err) {
      toast({ variant: "destructive", title: "Delete failed", description: err.message });
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">Companies</h1>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by company name, admin name, or email..."
          className="h-8 text-xs pl-9"
        />
      </div>

      <Card className="border-gray-100 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-500">Company</th>
                    <th className="text-left p-3 font-medium text-gray-500">Admin</th>
                    <th className="text-left p-3 font-medium text-gray-500">Industry</th>
                    <th className="text-left p-3 font-medium text-gray-500">Users</th>
                    <th className="text-left p-3 font-medium text-gray-500">Leads</th>
                    <th className="text-left p-3 font-medium text-gray-500">Admin Last Login</th>
                    <th className="text-left p-3 font-medium text-gray-500">Created</th>
                    <th className="text-left p-3 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="p-3">
                        <p className="font-medium text-gray-800">{c.company_name}</p>
                        <p className="text-[10px] text-gray-400">{c.company_email}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-gray-700">{c.admin_name || "-"}</p>
                        <p className="text-[10px] text-gray-400">{c.admin_email}</p>
                      </td>
                      <td className="p-3">
                        {c.industry_type ? (
                          <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px]">
                            {c.industry_type}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3 font-medium text-gray-700">{c.user_count}</td>
                      <td className="p-3 font-medium text-gray-700">{c.lead_count}</td>
                      <td className="p-3 text-gray-500">
                        {c.admin_last_login
                          ? new Date(c.admin_last_login).toLocaleString()
                          : "Never"}
                      </td>
                      <td className="p-3 text-gray-500">{formatDate(c.created_at)}</td>
                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Delete company"
                          onClick={() =>
                            setDeleteConfirm({
                              id: c.id,
                              name: c.company_name,
                              leads: c.lead_count,
                              users: c.user_count,
                            })
                          }
                        >
                          <Trash2 size={13} className="text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-6 text-center text-gray-400">
                        No companies found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Page {pagination.page} of {pagination.pages} ({pagination.total} companies)
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); loadCompanies(page - 1); }}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page >= pagination.pages}
              onClick={() => { setPage(page + 1); loadCompanies(page + 1); }}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteConfirm?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete the company and all associated data:
              <br />
              - Business admin and {deleteConfirm?.users || 0} team member(s)
              <br />
              - {deleteConfirm?.leads || 0} lead(s)
              <br />
              - All custom fields and options
              <br /><br />
              Data will be kept in Trash for 60 days and can be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
